import styled from 'styled-components';
import React, { useRef, useState, useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
import PropTypes from '@userzoomlibraries/toolkit-js-utils/prop-types';
import createAttribute from '@userzoomlibraries/toolkit-js-utils/create-attribute';
import noop from '@userzoomlibraries/toolkit-js-utils/noop';
import useAriaAttributes from '@userzoomlibraries/toolkit-react-utils/use-aria-attributes';
import useComparePrevious from '@userzoomlibraries/toolkit-react-utils/use-compare-previous';
import useDataAttributes from '@userzoomlibraries/toolkit-react-utils/use-data-attributes';
import useFocusable from '@userzoomlibraries/toolkit-react-utils/use-focusable';
import isDefinedAndNotNull from '@userzoomlibraries/toolkit-js-utils/is-defined-and-not-null';
import { u as useTabs } from './index.js';
import { u as useIndicator } from './index2.js';
import { jsx } from 'react/jsx-runtime';

function _toConsumableArray(arr) { return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray$1(arr) || _nonIterableSpread(); }

function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$1(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$1(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$1(o, minLen); }

function _iterableToArray(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithoutHoles(arr) { if (Array.isArray(arr)) return _arrayLikeToArray$1(arr); }

function _arrayLikeToArray$1(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

var range = function range(min, max) {
  return _toConsumableArray(Array(max - min + 1).keys()).map(function (i) {
    return i + min;
  });
};

var generateId = function generateId() {
  var characters = [].concat(_toConsumableArray(range(0, 9).map(function (each) {
    return "".concat(each);
  })), _toConsumableArray(range(65, 90).map(function (each) {
    return String.fromCharCode(each);
  })), _toConsumableArray(range(97, 122).map(function (each) {
    return String.fromCharCode(each);
  })));
  var crypto = window.crypto || window.msCrypto; // Shuffle the array

  characters.sort(function () {
    var array = new Uint8Array(1);
    var randomNumber = crypto.getRandomValues(array);
    return 0.5 - randomNumber.at(0) / (Math.pow(2, 8) - 1);
  });
  return characters.slice(0, 8).join('');
};

var useId = function useId(paramId) {
  var id = useRef(paramId || generateId());
  return id.current;
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var TabContext = /*#__PURE__*/React.createContext();

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var _verticalScrollTab = function _verticalScrollTab() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      tabElement = _ref.tabElement,
      forceActiveTabCentered = _ref.forceActiveTabCentered;

  if (forceActiveTabCentered) {
    var tabListElement = tabElement.closest('[role="tablist"]'); // Scroll to the selected tab, centering it in the middle of the tab list

    tabListElement.scrollTo({
      left: 0,
      top: tabElement.offsetTop + tabElement.offsetHeight / 2 - tabListElement.clientHeight / 2,
      behavior: 'smooth'
    });
  } else {
    tabElement.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
  }
};

var _horizontalScrollTab = function _horizontalScrollTab() {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      tabElement = _ref2.tabElement,
      forceActiveTabCentered = _ref2.forceActiveTabCentered;

  if (forceActiveTabCentered) {
    tabElement.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    });
  } else {
    tabElement.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
  }
};

var scrollTab = function scrollTab() {
  var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      tabElement = _ref3.tabElement,
      isVertical = _ref3.isVertical,
      forceActiveTabCentered = _ref3.forceActiveTabCentered;

  if (isVertical) {
    _verticalScrollTab({
      tabElement: tabElement,
      forceActiveTabCentered: forceActiveTabCentered
    });
  } else {
    _horizontalScrollTab({
      tabElement: tabElement,
      forceActiveTabCentered: forceActiveTabCentered
    });
  }
};

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(Object(source), true).forEach(function (key) { _defineProperty$1(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$1(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var keyboardInteraction = function keyboardInteraction() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      activeTab = _ref.activeTab,
      currentTab = _ref.currentTab,
      event = _ref.event,
      isVertical = _ref.isVertical,
      manualSelection = _ref.manualSelection,
      forceActiveTabCentered = _ref.forceActiveTabCentered,
      onChange = _ref.onChange,
      setTabs = _ref.setTabs,
      tabs = _ref.tabs;

  var isBackspaceKeyPressed = event.key === 'Backspace' || event.code === 'Backspace' || event.keyCode === 8;
  var isDownArrowPressed = event.key === 'ArrowDown' || event.code === 'ArrowDown' || event.keyCode === 40;
  var isEndPressed = event.key === 'End' || event.code === 'End' || event.keyCode === 35;
  var isEnterKeyPressed = event.key === 'Enter' || event.code === 'Enter' || event.keyCode === 13;
  var isHomePressed = event.key === 'Home' || event.code === 'Home' || event.keyCode === 36;
  var isLeftArrowPressed = event.key === 'ArrowLeft' || event.code === 'ArrowLeft' || event.keyCode === 37;
  var isRightArrowPressed = event.key === 'ArrowRight' || event.code === 'ArrowRight' || event.keyCode === 39;
  var isUpArrowPressed = event.key === 'ArrowUp' || event.code === 'ArrowUp' || event.keyCode === 38;
  var isNextElementPressed = isVertical ? isDownArrowPressed : isRightArrowPressed;
  var isPreviousElementPressed = isVertical ? isUpArrowPressed : isLeftArrowPressed;
  var anyAccessibilityKeyPressed = isBackspaceKeyPressed || isEndPressed || isEnterKeyPressed || isHomePressed || isNextElementPressed || isPreviousElementPressed;

  if (anyAccessibilityKeyPressed) {
    event.preventDefault();
    var tabElements = Array.from(event.target.parentNode.querySelectorAll('[role="tab"]:enabled'));
    var currentTabIndex = tabElements.findIndex(function (el) {
      return el.id === currentTab.id;
    });
    var firstTab = tabElements[0];
    var nextTab = tabElements[currentTabIndex + 1];
    var previousTab = tabElements[currentTabIndex - 1];
    var lastTab = tabElements[tabElements.length - 1];
    var selectedTab = currentTab;

    if (isPreviousElementPressed) {
      if (selectedTab.id === firstTab.id) {
        selectedTab = lastTab;
      } else {
        selectedTab = previousTab;
      }
    }

    if (isNextElementPressed) {
      if (selectedTab.id === lastTab.id) {
        selectedTab = firstTab;
      } else {
        selectedTab = nextTab;
      }
    }

    if (isHomePressed) {
      selectedTab = firstTab;
    }

    if (isEndPressed) {
      selectedTab = lastTab;
    }

    selectedTab.focus();
    scrollTab({
      tabElement: selectedTab,
      isVertical: isVertical,
      forceActiveTabCentered: forceActiveTabCentered
    });
    var newState = tabs.map(function (el) {
      return _objectSpread$1(_objectSpread$1({}, el), {}, {
        isActive: el.id === selectedTab.id
      });
    });

    if (manualSelection) {
      if (isEnterKeyPressed || isBackspaceKeyPressed) {
        if (isDefinedAndNotNull(activeTab)) {
          onChange(selectedTab.id);
        } else {
          setTabs(newState);
        }
      }
    } else if (isDefinedAndNotNull(activeTab)) {
      onChange(selectedTab.id);
    } else {
      setTabs(newState);
    }
  }
};

var _excluded = ["children", "className", "disabled", "id", "label", "onClick"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
var Tab$1 = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var children = _ref.children,
      className = _ref.className,
      isTabDisabled = _ref.disabled,
      id = _ref.id,
      label = _ref.label,
      onClick = _ref.onClick,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(),
      _useState2 = _slicedToArray(_useState, 2),
      currentTab = _useState2[0],
      setCurrentTab = _useState2[1];

  var ariaAttributes = useAriaAttributes(rest);
  var dataAttributes = useDataAttributes(rest);
  var tabRef = useRef();

  var _useTabs = useTabs(),
      activeTab = _useTabs.activeTab,
      isTabsDisabled = _useTabs.disabled,
      isVertical = _useTabs.isVertical,
      manualSelection = _useTabs.manualSelection,
      forceActiveTabCentered = _useTabs.forceActiveTabCentered,
      onChange = _useTabs.onChange,
      registerTab = _useTabs.registerTab,
      setTabs = _useTabs.setTabs,
      tabs = _useTabs.tabs,
      unregisterTab = _useTabs.unregisterTab,
      updateTab = _useTabs.updateTab;

  var _useIndicator = useIndicator(),
      _useIndicator2 = _slicedToArray(_useIndicator, 2),
      indicatorShouldRecalculate = _useIndicator2[0].shouldRecalculate,
      setIndicator = _useIndicator2[1];

  var disabled = isTabDisabled || isTabsDisabled;
  var propId = useId(id);
  var tabIdRef = useRef(propId);
  var focusableProps = useFocusable(tabRef.current, _objectSpread(_objectSpread({}, rest), {}, {
    disabled: disabled,
    role: 'tab'
  }));
  var isDisabled = focusableProps['aria-disabled'];

  var onTabClick = function onTabClick(event) {
    if (!isDefinedAndNotNull(activeTab) && !disabled) {
      var newState = tabs.map(function (el) {
        return _objectSpread(_objectSpread({}, el), {}, {
          isActive: el.id === currentTab.id
        });
      });
      setTabs(newState);
      scrollTab({
        tabElement: tabRef.current,
        isVertical: isVertical,
        forceActiveTabCentered: forceActiveTabCentered
      });
      onClick({
        event: event,
        id: id
      });
    } else {
      onChange(id);
    }
  };

  var onKeyDownHandler = useCallback(function (event) {
    keyboardInteraction({
      activeTab: activeTab,
      currentTab: currentTab,
      event: event,
      manualSelection: manualSelection,
      forceActiveTabCentered: forceActiveTabCentered,
      onChange: onChange,
      isVertical: isVertical,
      setTabs: setTabs,
      tabs: tabs
    });
  }, [activeTab, currentTab, manualSelection, onChange, isVertical, forceActiveTabCentered, setTabs, tabs]);
  var calculateIndicatorPosition = useCallback(function () {
    var tabToProcess = null;
    var isDisabledToProcess = null;

    if (currentTab !== null && currentTab !== void 0 && currentTab.isActive) {
      tabToProcess = tabRef.current;
      isDisabledToProcess = isDisabled;
    } else if (indicatorShouldRecalculate) {
      var activeTabItem = tabs.find(function (eachTab) {
        return eachTab.isActive;
      });

      if (activeTabItem) {
        tabToProcess = activeTabItem.element;
        isDisabledToProcess = activeTabItem.isDisabled;
      }
    }

    if (tabToProcess) {
      var _tabToProcess$getBoun = tabToProcess.getBoundingClientRect(),
          width = _tabToProcess$getBoun.width,
          height = _tabToProcess$getBoun.height;

      var _tabToProcess = tabToProcess,
          offsetTop = _tabToProcess.offsetTop,
          offsetLeft = _tabToProcess.offsetLeft,
          offsetRight = _tabToProcess.offsetRight;

      if (isVertical) {
        setIndicator({
          height: height,
          top: offsetTop,
          disabled: isDisabledToProcess,
          shouldRecalculate: false
        });
      } else {
        setIndicator({
          left: offsetLeft,
          right: offsetRight,
          width: width,
          disabled: isDisabledToProcess,
          shouldRecalculate: false
        });
      }
    }
  }, [currentTab, isDisabled, isVertical, indicatorShouldRecalculate, tabs, setIndicator]);
  useEffect(function () {
    updateTab(tabIdRef.current, {
      disabled: isDisabled
    });
  }, [isDisabled, updateTab]);
  useEffect(function () {
    updateTab(tabIdRef.current, {
      dataAttributes: dataAttributes
    });
  }, [useComparePrevious(dataAttributes), updateTab]);
  useEffect(function () {
    var tab = tabs.find(function (el) {
      return el.id === tabIdRef.current;
    });
    setCurrentTab(tab);
  }, [tabs]);
  useEffect(function () {
    var tabId = tabIdRef.current;
    var isActive = activeTab === tabId;
    updateTab(tabId, {
      isActive: isActive
    });

    if (isActive) {
      scrollTab({
        tabElement: tabRef.current,
        isVertical: isVertical,
        forceActiveTabCentered: forceActiveTabCentered
      });
    }
  }, [activeTab, isVertical, forceActiveTabCentered, updateTab]);
  useEffect(function () {
    calculateIndicatorPosition();
  }, [calculateIndicatorPosition]);
  useEffect(function () {
    var tabElement = tabRef.current;
    var tabId = tabIdRef.current;
    registerTab({
      dataAttributes: dataAttributes,
      id: tabId,
      viewer: children,
      element: tabRef.current
    });
    var resizeObserver = new ResizeObserver(function () {
      setIndicator(function (prevState) {
        return _objectSpread(_objectSpread({}, prevState), {}, {
          shouldRecalculate: true
        });
      });
    });
    resizeObserver.observe(tabElement);
    return function () {
      unregisterTab(tabId);
      resizeObserver.unobserve(tabElement);
    };
  }, []);
  useImperativeHandle(ref, function () {
    return tabRef.current;
  });
  var isCurrentTabActive = currentTab === null || currentTab === void 0 ? void 0 : currentTab.isActive;
  var providerState = useMemo(function () {
    return {
      id: tabIdRef.current,
      disabled: isDisabled,
      isActive: isCurrentTabActive,
      onClick: onClick
    };
  }, [isDisabled, onClick, isCurrentTabActive]);
  var classnames = "gz-tabs__tab ".concat(className);

  if (isCurrentTabActive) {
    classnames = "".concat(classnames, " gz-tabs__tab--active");
  }

  if (disabled) {
    classnames = "".concat(classnames, " gz-tabs__tab--disabled");
  }

  if (isVertical) {
    classnames = "".concat(classnames, " gz-tabs__tab--vertical");
  }

  return (
    /*#__PURE__*/
    // eslint-disable-next-line jsx-a11y/role-supports-aria-props
    jsx("button", _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
      "aria-controls": "".concat(tabIdRef.current, "-viewer"),
      "aria-selected": isCurrentTabActive,
      id: tabIdRef.current,
      ref: tabRef,
      className: classnames,
      onClick: onTabClick,
      onKeyDown: onKeyDownHandler,
      type: "button" // eslint-disable-next-line react/jsx-props-no-spreading

    }, ariaAttributes), dataAttributes), focusableProps), createAttribute({
      attribute: 'tabIndex',
      value: !isCurrentTabActive ? '-1' : undefined
    })), {}, {
      children: /*#__PURE__*/jsx(TabContext.Provider, {
        value: providerState,
        children: label
      })
    }))
  );
});
Tab$1.COMPONENT_ID = 'Gearz.Tabs.Tab';
Tab$1.displayName = Tab$1.COMPONENT_ID;
Tab$1.propTypes = {
  id: PropTypes.stringWithDescription('Id of the tab.').isRequired,
  className: PropTypes.stringWithDescription('Custom class to be applied.'),
  label: PropTypes.nodeWithDescription('Title of the tab.'),
  disabled: PropTypes.boolWithDescription('Flag to make the tab disable.'),
  onClick: PropTypes.funcWithDescription('Callback called when click event is triggered.'),
  children: PropTypes.nodeWithDescription('Content of the tab.')
};
Tab$1.defaultProps = {
  className: '',
  label: null,
  disabled: false,
  onClick: noop,
  children: null
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var Tab = styled(Tab$1).withConfig({
  componentId: "gz-tabs-1677758460838__sc-gmsoyz-0"
})(["background-color:initial;border:none;color:#5b6c7f;list-style:none;padding:4px;position:relative;text-align:center;user-select:none;&.gz-tabs__tab--active{background-color:#fff;color:#314fff;}&:not(.gz-tabs__tab--disabled):not(.gz-tabs__tab--active){&:hover{background-color:#e3e3e3;color:#314fff;cursor:pointer;}}&.gz-tabs__tab--disabled{color:#b6b6b6;cursor:not-allowed;}&.gz-tabs__tab--vertical{text-align:left;width:100%;}&:focus-visible{border-radius:4px;outline:2px solid #314fff;outline-offset:-2px;z-index:5;}"]);
Tab.displayName = Tab$1.displayName;
Tab.propTypes = Tab$1.propTypes;
Tab.defaultProps = Tab$1.defaultProps;

export { Tab as T };
